// Przygotowanie pliku do bucketu `help-media`. Nazwa z hasha zawartości — tak
// jak dziś robi `scripts/help/add-media.mjs` — więc ten sam plik wgrany dwa razy
// nie mnoży się w storage, a podmiana treści zawsze daje nowy adres.
export const HELP_MEDIA_EXTENSIONS = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "video/mp4": "mp4",
  "text/vtt": "vtt",
} as const;

export type HelpMediaMime = keyof typeof HELP_MEDIA_EXTENSIONS;

export const isHelpMediaMime = (mimeType: string): mimeType is HelpMediaMime =>
  Object.prototype.hasOwnProperty.call(HELP_MEDIA_EXTENSIONS, mimeType);

export const helpMediaKind = (mimeType: string): "image" | "video" | "captions" | "unknown" => {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType === "video/mp4") return "video";
  if (mimeType === "text/vtt") return "captions";
  return "unknown";
};

/** 20 znaków hasha wystarczy na unikalność, a nazwa zostaje czytelna w logach. */
export const buildHelpMediaPath = (checksum: string, mimeType: HelpMediaMime): string =>
  `${checksum.slice(0, 20)}.${HELP_MEDIA_EXTENSIONS[mimeType]}`;

export const sha256Hex = async (data: ArrayBuffer): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
};

export interface PreparedHelpMedia {
  file: Blob;
  storagePath: string;
  mimeType: HelpMediaMime;
  bytes: number;
  width: number | null;
  height: number | null;
  durationSeconds: number | null;
  checksum: string;
}

const readImage = (blob: Blob) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Nie udało się odczytać obrazu."));
    };
    image.src = url;
  });

const readVideo = (blob: Blob) =>
  new Promise<HTMLVideoElement>((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Nie udało się odczytać filmu."));
    };
    video.src = url;
  });

// Przerysowanie przez canvas gubi EXIF (w tym GPS z telefonu) i przy okazji daje
// wymiary, których wymaga renderer po stronie klientki — bez nich układ skacze
// przy dogrywaniu obrazka.
const stripImageMetadata = async (blob: Blob, mimeType: HelpMediaMime) => {
  const image = await readImage(blob);
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Przeglądarka nie udostępniła canvasu do usunięcia metadanych.");
  }
  context.drawImage(image, 0, 0);
  const encoded = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, mimeType, mimeType === "image/png" ? undefined : 0.92);
  });
  if (!encoded) {
    throw new Error("Nie udało się przetworzyć obrazu.");
  }
  return { blob: encoded, width: image.naturalWidth, height: image.naturalHeight };
};

/**
 * Plakat z pierwszej klatki nagrania (TRE-2137). Dotąd redakcja wycinała go
 * ręcznie do każdego filmu, a bez plakatu picker nie pozwalał wstawić wideo.
 *
 * Czekamy na `loadeddata`, nie na `loadedmetadata`: to drugie daje wymiary,
 * ale jeszcze nie zdekodowaną klatkę, więc canvas wyszedłby czarny. WebP,
 * bo ręcznie wycinane plakaty PNG potrafiły ważyć więcej niż samo nagranie.
 */
export const posterFromVideo = async (file: File): Promise<PreparedHelpMedia> => {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.src = url;

    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error("Nie udało się odczytać filmu."));
    });

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Przeglądarka nie udostępniła canvasu do wycięcia plakatu.");
    }
    context.drawImage(video, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/webp", 0.82));
    if (!blob) {
      throw new Error("Nie udało się wyciąć plakatu z nagrania.");
    }

    const checksum = await sha256Hex(await blob.arrayBuffer());
    return {
      file: blob,
      storagePath: buildHelpMediaPath(checksum, "image/webp"),
      mimeType: "image/webp",
      bytes: blob.size,
      width: video.videoWidth,
      height: video.videoHeight,
      durationSeconds: null,
      checksum,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const prepareHelpMedia = async (file: File): Promise<PreparedHelpMedia> => {
  const mimeType = file.type;
  if (!isHelpMediaMime(mimeType)) {
    throw new Error(`Nieobsługiwany format pliku: ${mimeType || "nieznany"}.`);
  }

  if (helpMediaKind(mimeType) === "image") {
    const { blob, width, height } = await stripImageMetadata(file, mimeType);
    const checksum = await sha256Hex(await blob.arrayBuffer());
    return {
      file: blob,
      storagePath: buildHelpMediaPath(checksum, mimeType),
      mimeType,
      bytes: blob.size,
      width,
      height,
      durationSeconds: null,
      checksum,
    };
  }

  const checksum = await sha256Hex(await file.arrayBuffer());
  const base = {
    file,
    storagePath: buildHelpMediaPath(checksum, mimeType),
    mimeType,
    bytes: file.size,
    checksum,
  };

  if (helpMediaKind(mimeType) === "video") {
    const video = await readVideo(file);
    return {
      ...base,
      width: video.videoWidth || null,
      height: video.videoHeight || null,
      // Czas trwania nagrania wchodzi do wpisu: renderer pokazuje go przy
      // filmie, żeby klientka wiedziała, na co się pisze.
      durationSeconds: Number.isFinite(video.duration) ? Math.round(video.duration * 10) / 10 : null,
    };
  }

  return { ...base, width: null, height: null, durationSeconds: null };
};
